import { PosVendaProtocoloGrupo, PosVendaProtocoloTipo } from '../../types/posVenda';

export interface TipoDemandaCatalogEntry {
  tipo: PosVendaProtocoloTipo;
  label: string;
  grupo: PosVendaProtocoloGrupo;
  /** Dias úteis para conclusão; null = rotina sem prazo fixo. */
  prazoConclusaoDiasUteis: number | null;
}

/** Catálogo Fase 1 — `tipo` é o nome do enum GettelApi.Core.Enums.PosVendaProtocoloTipo. */
export const TIPO_DEMANDA_CATALOGO: TipoDemandaCatalogEntry[] = [
  // Operacional
  { tipo: 'EnvioMeuTim', label: 'Envio Meu TIM', grupo: 'Operacional', prazoConclusaoDiasUteis: 1 },
  { tipo: 'Portabilidade', label: 'Portabilidade', grupo: 'Operacional', prazoConclusaoDiasUteis: 2 },
  { tipo: 'UnificacaoFatura', label: 'Unificação de fatura', grupo: 'Operacional', prazoConclusaoDiasUteis: 2 },
  { tipo: 'AnaliseFaturaSegundaVia', label: '2ª via de fatura', grupo: 'Operacional', prazoConclusaoDiasUteis: 1 },
  { tipo: 'TimNegocia', label: 'TIM Negocia', grupo: 'Operacional', prazoConclusaoDiasUteis: 1 },
  { tipo: 'RoamingInternacional', label: 'Roaming internacional', grupo: 'Operacional', prazoConclusaoDiasUteis: 2 },
  { tipo: 'ReposicaoChip', label: 'Reposição de chip', grupo: 'Operacional', prazoConclusaoDiasUteis: 1 },
  { tipo: 'TrocaChip', label: 'Troca de chip', grupo: 'Operacional', prazoConclusaoDiasUteis: 1 },
  { tipo: 'AnaliseFatura', label: 'Análise de fatura', grupo: 'Operacional', prazoConclusaoDiasUteis: 5 },
  { tipo: 'InformacaoDuvida', label: 'Dúvidas básicas', grupo: 'Operacional', prazoConclusaoDiasUteis: 1 },
  { tipo: 'ResetDeLinha', label: 'Reset de linha', grupo: 'Operacional', prazoConclusaoDiasUteis: 2 },
  // Logístico
  {
    tipo: 'SilentAtivacaoChip',
    label: 'Silent ativação chip',
    grupo: 'Logistico',
    prazoConclusaoDiasUteis: 1,
  },
  { tipo: 'ConfirmacaoEndereco', label: 'Confirmação de endereço', grupo: 'Logistico', prazoConclusaoDiasUteis: 3 },
  { tipo: 'ConfirmacaoEntrega', label: 'Confirmação de entrega', grupo: 'Logistico', prazoConclusaoDiasUteis: 3 },
  { tipo: 'ColetaMeuTim', label: 'Coleta Meu TIM', grupo: 'Logistico', prazoConclusaoDiasUteis: 1 },
  { tipo: 'Suspensao', label: 'Suspensão', grupo: 'Logistico', prazoConclusaoDiasUteis: 3 },
  { tipo: 'EnvioViaCorreio', label: 'Envio via correio', grupo: 'Logistico', prazoConclusaoDiasUteis: 3 },
  {
    tipo: 'SilentAtivacaoChip',
    label: 'Ativação silenciosa de chip',
    grupo: 'Logistico',
    prazoConclusaoDiasUteis: null,
  },
  {
    tipo: 'AcompanhamentoEstoqueFastchip',
    label: 'Acompanhamento estoque Fastchip',
    grupo: 'Logistico',
    prazoConclusaoDiasUteis: null,
  },
  // Supervisão
  {
    tipo: 'OrientarClienteCancelar1056',
    label: 'Orientar cliente a cancelar no 1056',
    grupo: 'Supervisao',
    prazoConclusaoDiasUteis: 2,
  },
  { tipo: 'Cancelamento', label: 'Cancelamento', grupo: 'Supervisao', prazoConclusaoDiasUteis: 2 },
  { tipo: 'Estorno', label: 'Estorno', grupo: 'Supervisao', prazoConclusaoDiasUteis: 2 },
  { tipo: 'Ressuspensao', label: 'Ressuspensão', grupo: 'Supervisao', prazoConclusaoDiasUteis: 2 },
  { tipo: 'Anatel', label: 'Anatel', grupo: 'Supervisao', prazoConclusaoDiasUteis: 1 },
  { tipo: 'Escalation', label: 'Escalation', grupo: 'Supervisao', prazoConclusaoDiasUteis: 2 },
  { tipo: 'RetornoReclamacao', label: 'Retorno de reclamação', grupo: 'Supervisao', prazoConclusaoDiasUteis: 2 },
  { tipo: 'ClienteCritico', label: 'Cliente crítico', grupo: 'Supervisao', prazoConclusaoDiasUteis: 2 },
];

const LEGACY_LABELS: Partial<Record<PosVendaProtocoloTipo, string>> = {
  Reativacao: 'Reativação (legado)',
  EnvioTrocaChip: 'Envio / troca de chip (legado)',
  Reclamacao: 'Reclamação (legado)',
  Outros: 'Outros (legado)',
};

/**
 * Nomes que a tela chegou a usar e que a API já cobre com outro membro do enum.
 * Não são tipos novos: o POST precisa do nome que o JsonStringEnumConverter aceita.
 */
const TIPO_ALIAS_API: Record<string, PosVendaProtocoloTipo> = {
  DuvidasBasicas: 'InformacaoDuvida',
  SegundaViaFatura: 'AnaliseFaturaSegundaVia',
};

export const GRUPO_DEMANDA_LABEL: Record<PosVendaProtocoloGrupo, string> = {
  Operacional: 'Operacional',
  Logistico: 'Logístico',
  Supervisao: 'Supervisão',
};

export function getTipoDemandaEntry(tipo: PosVendaProtocoloTipo): TipoDemandaCatalogEntry | undefined {
  return TIPO_DEMANDA_CATALOGO.find((e) => e.tipo === tipoCanonico(tipo));
}

function tipoCanonico(tipo: PosVendaProtocoloTipo): PosVendaProtocoloTipo {
  const alias = TIPO_ALIAS_API[String(tipo)];
  if (alias) return alias;
  const needle = String(tipo).toLowerCase();
  const aliasInsensivel = Object.entries(TIPO_ALIAS_API).find(([key]) => key.toLowerCase() === needle);
  if (aliasInsensivel) return aliasInsensivel[1];
  const catalogo = TIPO_DEMANDA_CATALOGO.find((e) => e.tipo.toLowerCase() === needle);
  if (catalogo) return catalogo.tipo;
  const legado = (Object.keys(LEGACY_LABELS) as PosVendaProtocoloTipo[]).find(
    (key) => key.toLowerCase() === needle
  );
  return legado ?? tipo;
}

/** Valor de `tipo` aceito por PosVendaProtocoloTipo na API. */
export function tipoProtocoloParaApi(tipo: string): PosVendaProtocoloTipo {
  return tipoCanonico(tipo as PosVendaProtocoloTipo);
}

export function getTipoDemandaLabel(tipo: PosVendaProtocoloTipo): string {
  const key = tipoCanonico(tipo);
  return getTipoDemandaEntry(key)?.label ?? LEGACY_LABELS[key] ?? String(tipo);
}

export function formatSlaDiasUteis(tipo: PosVendaProtocoloTipo): string {
  const entry = getTipoDemandaEntry(tipoCanonico(tipo));
  if (!entry) return '—';
  if (entry.prazoConclusaoDiasUteis == null) return 'Rotina (sem prazo fixo)';
  const n = entry.prazoConclusaoDiasUteis;
  return `${n} ${n === 1 ? 'dia útil' : 'dias úteis'}`;
}

export function tiposPorGrupo(): Record<PosVendaProtocoloGrupo, TipoDemandaCatalogEntry[]> {
  const map: Record<PosVendaProtocoloGrupo, TipoDemandaCatalogEntry[]> = {
    Operacional: [],
    Logistico: [],
    Supervisao: [],
  };
  TIPO_DEMANDA_CATALOGO.forEach((e) => map[e.grupo].push(e));
  return map;
}

/** Tipos em que o protocolo Meu TIM costuma ser preenchido na conclusão. */
export function tipoExigeProtocoloMeuTimNaConclusao(tipo: PosVendaProtocoloTipo): boolean {
  const t = tipoCanonico(tipo);
  return (
    t === 'EnvioMeuTim' ||
    t === 'ColetaMeuTim' ||
    t === 'Suspensao' ||
    t === 'Ressuspensao' ||
    t === 'Portabilidade'
  );
}

/** Tipos isentos da regra de anexo na conclusão (fluxo logístico / GettelApi PR #540). */
const TIPO_ISENTO_ANEXO_CONCLUSAO_ATENDIMENTO = new Set<PosVendaProtocoloTipo>([
  'EnvioViaCorreio',
  'SilentAtivacaoChip',
  'ConfirmacaoEndereco',
  'ConfirmacaoEntrega',
  'OrientarClienteCancelar1056',
]);

/** Protocolos de atendimento exigem ≥1 anexo ao concluir, salvo tipos isentos acima. */
export function tipoExigeAnexoNaConclusaoAtendimento(tipo: PosVendaProtocoloTipo): boolean {
  return !TIPO_ISENTO_ANEXO_CONCLUSAO_ATENDIMENTO.has(tipoCanonico(tipo));
}

export function isTipoSuspensao(tipo: PosVendaProtocoloTipo): boolean {
  return tipoCanonico(tipo) === 'Suspensao';
}

export function isTipoOrientarClienteCancelar1056(tipo: PosVendaProtocoloTipo): boolean {
  return tipoCanonico(tipo) === 'OrientarClienteCancelar1056';
}

export function tipoExigeDesfecho1056NaConclusao(tipo: PosVendaProtocoloTipo): boolean {
  return isTipoOrientarClienteCancelar1056(tipo);
}
